import { getAdder, getDiffer, getUnitStart } from "../time";
import { format, addMinutes, differenceInMinutes, isAfter, isSameDay, isBefore, isWithinInterval, } from "date-fns";
import { sortEvents, stackEvents } from "../events";
import { getDeepCopy, uid } from "./../helpers";
import { EventStore } from "./eventStore";
import { getNodeByAttr, locateID, elementFromPosition } from "@xbs/lib-dom";
class TimelineView {
    constructor() {
        this.getBounds = (date, config) => {
            const { step, colsCount, getBounds } = config;
            if (getBounds)
                return getBounds(date, config);
            const [increase, factor] = step;
            const start = getUnitStart(factor, date);
            const add = getAdder(factor);
            const end = add(start, increase * colsCount);
            return [start, end];
        };
        this.getNext = (date, config) => {
            const { step, getNext } = config;
            if (getNext)
                return getNext(date, config);
            const [increase, factor] = step;
            const add = getAdder(factor);
            return add(date, increase);
        };
        this.getPrev = (date, config) => {
            const { step, getPrev } = config;
            if (getPrev)
                return getPrev(date, config);
            const [increase, factor] = step;
            const add = getAdder(factor);
            return add(date, -increase);
        };
        this.getEventFromPosition = (id, trigger, e, locale) => {
            let event;
            if (trigger === "data-cell") {
                this._eventsStore.remove(this._unsavedEvent?.id);
                const { minutePixels } = this._layoutSizes;
                const cellDate = new Date(id);
                if (!cellDate.valueOf())
                    return;
                const date = addMinutes(cellDate, e.offsetX / minutePixels);
                const section = this._getSectionByPos(e);
                const sectionId = section?.id;
                const eventData = {
                    text: locale("New Event"),
                    start_date: date,
                    end_date: date,
                    dragMove: true,
                    section: sectionId,
                    id: uid(),
                };
                if (this._calendars.length) {
                    eventData.type = this._calendars[0].id;
                }
                const row = this._rows.find(r => r.section.id == sectionId);
                const normalizedEvent = this.normalizeEvent(eventData);
                const top = `${row?.top || 0}px`;
                event = {
                    ...normalizedEvent,
                    position: {
                        ...normalizedEvent.position,
                        top,
                        width: "2px",
                    },
                };
                this._eventsStore.add(event);
                this._unsavedEvent = eventData;
            }
            else {
                event = this.getEventById(id);
                this._unsavedEvent = {
                    ...event,
                    eventData: { ...event.eventData },
                };
            }
            return event;
        };
        this.updateEventPosition = (event, e, dragInfo, dragType) => {
            if (!event)
                return;
            let diff = { left: 0, top: 0, ...dragInfo };
            const { eventData, position } = event;
            const { start_date, end_date, id } = eventData;
            const [start_range] = this._bounds;
            const { clientY, clientX } = e;
            const { minutePixels, tableSize } = this._layoutSizes;
            const { sectionWidth } = this._config;
            if (dragType !== "update") {
                document.body.style.cursor = "w-resize";
                const end = this._getDateByPos(end_date, e);
                const width = differenceInMinutes(end, start_date) * minutePixels;
                if (isAfter(end, start_date)) {
                    event.position = {
                        ...event.position,
                        width: `${width}px`,
                    };
                    event.eventData.end_date = end;
                    this._eventsStore.update(event);
                    return;
                }
            }
            else {
                const minDiff = differenceInMinutes(end_date, start_date);
                const { node: tableNode } = elementFromPosition(e, "wx-table-auto-scroll");
                if (!event.eventData?.[this._key]) {
                    if (locateID(e, "data-id")) {
                        const { eventHeight, minEventWidth } = this._config;
                        this._unassigned.remove(id);
                        this._draggedItem.set([
                            {
                                ...event,
                                position: {
                                    left: `${clientX}px`,
                                    top: `${clientY}px`,
                                    height: `${eventHeight}px`,
                                    width: `${minEventWidth}px`,
                                },
                            },
                        ]);
                        diff = { top: this._config.eventHeight / 2, left: 0 };
                    }
                }
                if (!tableNode) {
                    const { node: listNode } = elementFromPosition(e, "wx-unassign-list");
                    if (listNode) {
                        if (this._unsavedEvent) {
                            event.eventData = { ...this._unsavedEvent.eventData };
                        }
                        event.eventData[this._key] = null;
                        this._eventsStore.remove(id);
                        const { eventHeight, minEventWidth } = this._config;
                        this._draggedItem.set([
                            {
                                ...event,
                                position: {
                                    left: `${clientX}px`,
                                    top: `${clientY}px`,
                                    height: `${eventHeight}px`,
                                    width: `${minEventWidth}px`,
                                },
                            },
                        ]);
                    }
                    return;
                }
                const timelineBody = getNodeByAttr("", tableNode, "wx-timeline-body");
                if (!timelineBody)
                    return;
                const rect = tableNode.getBoundingClientRect();
                const top = clientY -
                    rect.top -
                    timelineBody.offsetTop -
                    diff.top +
                    tableNode.scrollTop;
                const section = this._getSectionByPos(e);
                if (section && section.id !== eventData[this._key]) {
                    event.eventData[this._key] = section.id;
                }
                if (top > 0) {
                    event.position = {
                        ...event.position,
                        top: `${top}px`,
                    };
                }
                let newStart = this._getDateByPos(start_date, e, diff.left);
                if (!newStart && this._draggedItem.length)
                    return; // if we cannot identify date and we have unassign than it's probably scrollbar or border
                // remove from dragged and unassign because it will appended in table
                if (this._draggedItem.length) {
                    this._unassigned.remove(id);
                    this._draggedItem.set([]);
                }
                let left = this.calculateLeftByDate(newStart);
                if (left < sectionWidth) {
                    newStart = addMinutes(start_range, -diff.left / minutePixels);
                    left = sectionWidth;
                }
                let endDate = addMinutes(newStart, minDiff);
                // disable drag out of range (it makes sense when we have event with min width in ui)
                if (isBefore(endDate, start_range)) {
                    endDate = addMinutes(start_range, 5);
                    newStart = addMinutes(endDate, -minDiff);
                }
                if (newStart && left + parseFloat(position.width) < tableSize) {
                    event.position = {
                        ...event.position,
                        left: `${left}px`,
                    };
                    eventData.start_date = newStart;
                    eventData.end_date = addMinutes(newStart, minDiff);
                }
                this._eventsStore.update(event);
            }
        };
        this.getPositionInLine = (date, dates) => {
            const start = new Date(date.getFullYear(), date.getMonth(), date.getDate()).valueOf();
            const col = dates.findIndex((d, index) => d.valueOf() <= start &&
                (!dates[index + 1] || dates[index + 1].valueOf() > start));
            return col;
        };
        this.normalizeEvent = (event) => {
            const { eventHeight, minEventWidth } = this._config;
            const { tableSize, minutePixels } = this._layoutSizes;
            const [range_start, range_end] = this._bounds;
            const height = eventHeight + "px";
            const { start_date: event_start, end_date: event_end } = event;
            const start_date = event_start < range_start ? range_start : event_start;
            let end_date = event_end > range_end ? range_end : event_end;
            // calculate left position
            const left = `${this.calculateLeftByDate(start_date)}px`;
            //calculate width
            const eventDiffInMinutes = differenceInMinutes(end_date, start_date);
            let width = eventDiffInMinutes * minutePixels;
            if (width < minEventWidth) {
                const leftNumber = parseFloat(left);
                // check if event close to the end of the range
                width =
                    minEventWidth + leftNumber < tableSize
                        ? minEventWidth
                        : tableSize - leftNumber;
                end_date = addMinutes(start_date, width / minutePixels);
            }
            width = width + "px";
            return {
                eventData: { ...event, $end_date: end_date },
                position: { left, height, width },
            };
        };
        this.getTitle = (date, config) => {
            const [start, end] = this.getBounds(date, this._config);
            const s = format(start, "d LLL y", { locale: config.locale });
            const e = format(end, "d LLL y", { locale: config.locale });
            return isSameDay(start, end) ? s : `${s} - ${e}`;
        };
        this.getViewModel = (_activeDate, bounds, config, displayedEvents, calendars) => {
            this._config = {
                ...config,
            };
            const { step, sections = [], colsCount, colsWidth, sectionWidth, header, key, } = this._config;
            this._key = key;
            this._calendars = calendars;
            displayedEvents = sortEvents(displayedEvents);
            this._unsavedEvent = null;
            const events = [];
            const unassigned = [];
            const recurring = [];
            displayedEvents.forEach(ev => {
                if (ev[key]) {
                    events.push(ev);
                }
                else {
                    ev = getDeepCopy(ev);
                    ev[this._key] = null;
                    if (ev.recurring) {
                        recurring.push({ eventData: ev });
                    }
                    else {
                        unassigned.push({ eventData: ev });
                    }
                }
            });
            const groupedRecurring = {};
            const groupedRecurringData = {};
            if (recurring.length) {
                recurring.forEach(({ eventData }) => {
                    const { parent_id } = eventData;
                    let store = groupedRecurring[parent_id];
                    let data = groupedRecurringData[parent_id];
                    if (!store) {
                        store = new EventStore();
                        data = [];
                        groupedRecurring[parent_id] = store;
                        groupedRecurringData[parent_id] = [];
                    }
                    data.push({ eventData });
                });
            }
            this._allUnassigned = new EventStore([...unassigned, ...recurring]);
            this._sections = sections.map((s, i) => ({ ...s, $index: i }));
            this._eventsStore = new EventStore();
            this._unassigned = new EventStore();
            this._draggedItem = new EventStore();
            this._unassignedRecurring = Object.values(groupedRecurring);
            // gets px per minute
            const [range_start, range_end] = bounds;
            const totalColsWidth = colsWidth * colsCount;
            const minutePixels = totalColsWidth / differenceInMinutes(range_end, range_start);
            const tableSize = totalColsWidth + sectionWidth;
            this._layoutSizes = {
                totalColsWidth,
                minutePixels,
                tableSize,
            };
            const [increase, factor] = step;
            const add = getAdder(factor);
            const dates = [];
            // eslint-disable-next-line prefer-const
            let [currentDate] = bounds;
            let curCol = 0;
            while (curCol < colsCount) {
                dates.push(currentDate);
                currentDate = add(currentDate, increase);
                curCol++;
            }
            this._bounds = bounds;
            this._dates = dates;
            const { events: normalizedEvents, rows } = this.normalizeData(events);
            this._init = {
                events: normalizedEvents,
                rows,
                unassigned,
                unassignedRecurring: Object.values(groupedRecurringData),
            };
            this._config.header = header.map(h => this.normalizeHeaderSection(h, dates));
            this.resetToInit();
            const minuteLine = isWithinInterval(new Date(), {
                start: dates[0],
                end: dates[dates.length - 1],
            });
            return {
                title: this.getTitle,
                calendars,
                config: this._config,
                events: this._eventsStore,
                rows: this._rows,
                unassigned: this._unassigned,
                unassignedRecurring: this._unassignedRecurring,
                dates,
                updateEventPosition: this.updateEventPosition.bind(this),
                getEventFromPosition: this.getEventFromPosition.bind(this),
                addEvent: this.addEvent.bind(this),
                removeEvent: this.removeEvent.bind(this),
                getEventById: this.getEventById.bind(this),
                resetToInit: this.resetToInit.bind(this),
                draggedItem: this._draggedItem,
                minuteLine,
                calculateMinutesLinePosition: this.calculateMinutesLinePosition.bind(this),
            };
        };
    }
    addEvent(event) {
        this._unassigned.add({ eventData: event });
    }
    removeEvent(id) {
        this._eventsStore.remove(id);
    }
    getEventById(id) {
        let event = this._eventsStore.getEvent(id);
        if (!event) {
            const unassigned = this._allUnassigned.getEvent(id);
            event = this.normalizeEvent(unassigned?.eventData);
        }
        return event;
    }
    _getSectionByPos(e) {
        const { clientX, clientY } = e;
        const elements = document.elementsFromPoint(clientX, clientY);
        let sectionId;
        for (let i = 0; i < elements.length; i++) {
            sectionId = locateID(elements[i], "data-section-id");
            if (sectionId)
                break;
        }
        return this._sections.find(({ id }) => id == sectionId);
    }
    _getDateByPos(date, e, xAsis = 0) {
        const { minutePixels } = this._layoutSizes;
        const { node: cell, id } = elementFromPosition(e, "data-cell");
        if (!cell)
            return;
        const { x } = cell.getBoundingClientRect();
        const diff = differenceInMinutes(new Date(+id), date);
        const pxlDiff = e.clientX - x - xAsis;
        return addMinutes(date, pxlDiff / minutePixels + diff);
    }
    calculateLeftByDate(date) {
        const { sectionWidth } = this._config;
        const { minutePixels } = this._layoutSizes;
        return (differenceInMinutes(date, this._dates[0]) * minutePixels +
            sectionWidth);
    }
    getCrossedEvents(events) {
        return stackEvents(events);
    }
    resetToInit() {
        const { events, rows, unassigned, unassignedRecurring } = this._init;
        this._rows = rows.map(r => ({ ...r, section: { ...r.section } }));
        this.storeReset(this._eventsStore, events);
        this.storeReset(this._unassigned, unassigned);
        this.storeReset(this._draggedItem, []);
        this._unassignedRecurring.forEach((store, index) => this.storeReset(store, unassignedRecurring[index]));
    }
    storeReset(store, data) {
        store.set(data.map(ev => ({
            position: { ...ev.position },
            eventData: { ...ev.eventData },
        })));
    }
    sortEventsBySections(events, sections) {
        const find = (id) => sections.findIndex(item => item.id === id);
        return events.sort((a, b) => find(a[this._key]) - find(b[this._key]));
    }
    normalizeData(events) {
        let sectionTop = 0;
        const newEvents = [];
        const rows = [];
        const { eventHeight, minSectionHeight } = this._config;
        this._sections.forEach(section => {
            const id = section.id;
            const filteredEvents = events.filter(ev => ev[this._key] === id);
            const normalizedEvents = filteredEvents.map(ev => this.normalizeEvent(ev));
            // stack events with new endData
            const crossedEvents = stackEvents(normalizedEvents.map(({ eventData }) => {
                const { $end_date, ...rest } = eventData;
                return { ...rest, end_date: $end_date };
            }));
            // calculate section height
            const max = Math.max(...Object.values(crossedEvents)) + 1;
            const maxHeight = max * eventHeight + (max - 1) * 6; // 6px margin;
            const sectionHeight = maxHeight > minSectionHeight ? maxHeight : minSectionHeight;
            //calculate top
            const calculatedEvents = normalizedEvents.map(({ eventData, position }) => {
                // clear data from temple var
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { $end_date, ...restEventData } = eventData;
                const id = restEventData.id;
                //calculate top position
                const crossIndex = crossedEvents[id];
                let top = crossIndex
                    ? crossIndex * (this._config.eventHeight + 6) +
                        sectionTop // 6px margin
                    : sectionTop;
                top = top + "px";
                return {
                    eventData: { ...restEventData },
                    position: { ...position, top },
                };
            });
            rows.push({
                height: sectionHeight,
                top: sectionTop,
                section,
            });
            sectionTop += sectionHeight;
            newEvents.push(...calculatedEvents);
        });
        return { events: newEvents, rows };
    }
    normalizeHeaderSection(header, dates) {
        const { unit, step } = header;
        const differ = getDiffer(unit);
        const cols = [];
        let colspan = 1;
        let prev;
        for (let i = 0; i < dates.length; i++) {
            const date = dates[i];
            if (differ(date, prev?.date) === step) {
                prev.colspan = colspan;
                prev = { date };
                cols.push(prev);
                colspan = 1;
            }
            else {
                colspan++;
            }
            if (i === dates.length - 1) {
                prev.colspan = colspan;
            }
            if (!i) {
                prev = { date };
                colspan--;
                cols.push(prev);
            }
        }
        return { ...header, cols };
    }
    calculateMinutesLinePosition() {
        return `${this.calculateLeftByDate(new Date())}px`;
    }
}
export default TimelineView;
//# sourceMappingURL=timeline.js.map